import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Grid, Input, Dropdown as DropdownNativo, Icon } from 'semantic-ui-react';
import { Field, FormikProps } from 'formik';
import SectionLabel from '../../../../../../components/SectionLabel';
import { FieldLabel } from '../../../../../../styles';
import {
  maskCpfOrCnpj,
  maskPhoneNumber,
  maskZipCode
} from '../../../../../../services/masks';
import { getDataByDocument } from '../../../../../../services/documents';
import { lookupClaimCondominium } from '../../../../../../services/claims';
import { AddressService } from '../../../../../../services/address';
import Button from '../../../../../../components/Button';
import FormatedTextArea from '../../../../../../components/FormatedTextArea';
import { CustoDropDownItemProps } from '../../../../../../components/types';
import { InsurerState } from '../../../../../../types/InsurerState';
import { UserState } from '../../../../../../types/UserState';
import { isLoggedEstateRole } from '../../../../../../utils/isEstateRole';
import { isBackOfficeUser } from '../../../../../../utils/isBackOfficeUser';
import { resolveClaimEstateId } from '../../utils/resolveClaimEstateId';
import { stateOptions } from '../../../../../estates-list/types';
import { getClaimInsurerDropdownOptions } from '../../../../utils/claimInsurerOptions';
import ClaimPropertyWalletSearchDropdown, {
  WalletTag
} from '../../../create-claim-modal/components/ClaimPropertyWalletSearchDropdown';
import { claimEstatesStatusEnum } from '../../../../constants/ClaimEstatesStatusEnum';
import { ClaimProductTypeEnum } from '../../../../enums/ClaimProductType.enum';
import { OtherProductClaimInitialValuesType } from '../../Formik/types';

type BasicInfoStepProps = FormikProps<OtherProductClaimInitialValuesType> & {
  estates: CustoDropDownItemProps[] | null;
  insurers: InsurerState[] | null;
  availableBrokersAndEstates?: any;
  user?: UserState;
  isEditMode?: boolean;
  users?: UserState[];
};

type ClaimSectionLabelProps = {
  text: string;
};

const BasicInfoStep = ({
  values,
  setFieldValue,
  estates,
  insurers,
  availableBrokersAndEstates,
  user,
  isEditMode = false,
  users = []
}: BasicInfoStepProps) => {
  const [loadDocument, setLoadDocument] = useState(false);
  const [disableNameField, setDisableNameField] = useState(true);
  const [loadingZipCode, setLoadingZipCode] = useState(false);
  const [selectableEstates, setSelectableEstates] = useState<any[]>([]);
  const [responsibleOptions, setResponsibleOptions] = useState<
    CustoDropDownItemProps[]
  >([]);
  const isEstateUser = isLoggedEstateRole();
  const isBackOffice = isBackOfficeUser();
  const canSelectResponsible = isBackOffice;
  const isCondominium = values.productType === ClaimProductTypeEnum.CONDOMINIUM;
  const inputRowStyle = isCondominium
    ? { paddingTop: 0, paddingBottom: 0 }
    : undefined;
  const selectedFromWallet = Boolean(values.claimEstate.propertyPublicId);
  const isFieldLocked = selectedFromWallet;
  const selectableInsurers = getClaimInsurerDropdownOptions(insurers);
  const condominiumLookupRequestRef = useRef(0);

  const ClaimSectionLabel = ({ text }: ClaimSectionLabelProps) => (
    <SectionLabel
      text={text}
      color={isCondominium ? '#59249B' : undefined}
      lineColor={isCondominium ? '#C8ACFD' : undefined}
      fullWidth={isCondominium}
      compact={isCondominium}
      paddingTop={isCondominium ? 25 : undefined}
      paddingBottom={isCondominium ? 10 : undefined}
      labelPaddingBottom={isCondominium ? 5 : undefined}
    />
  );

  const clearSelectedProperty = () => {
    setFieldValue('claimEstate.propertyPublicId', undefined);
    setFieldValue('claimProperty.propertyCode', '');
    setFieldValue('claimProperty.zipCode', '');
    setFieldValue('claimProperty.street', '');
    setFieldValue('claimProperty.district', '');
    setFieldValue('claimProperty.city', '');
    setFieldValue('claimProperty.state', '');
    setFieldValue('claimProperty.number', '');
    setFieldValue('claimProperty.complement', '');
  };

  const setCondominiumField = (field: string, value: string) => {
    setFieldValue(`claimCondominium.${field}`, value);

    // Mantém os campos legados preenchidos enquanto a API ainda exige
    // claimTenant para os produtos de sinistro existentes.
    if (field === 'document') setFieldValue('claimTenant.document', value);
    if (field === 'legalName') setFieldValue('claimTenant.fullName', value);
    if (field === 'email') setFieldValue('claimTenant.email', value);
    if (field === 'phone') setFieldValue('claimTenant.cellphone', value);
  };

  const clearCondominiumFields = (keepDocument = false) => {
    if (!keepDocument) setCondominiumField('document', '');
    setCondominiumField('legalName', '');
    setCondominiumField('email', '');
    setCondominiumField('phone', '');
  };

  useEffect(() => {
    if (isCondominium) {
      if (
        values.claimCondominium.document &&
        !values.claimCondominium.legalName
      ) {
        getCondominiumByDocument(values.claimCondominium.document);
      }
      return;
    }

    if (values.claimTenant.document && !values.claimTenant.fullName) {
      getInsuredByDocument(values.claimTenant.document);
    } else if (values.claimTenant.fullName) {
      setDisableNameField(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCondominium]);

  useEffect(() => {
    if (estates?.length) {
      setSelectableEstates(
        estates.map(estate => ({
          index: estate.value.id,
          value: estate.value.id,
          text: estate.value.name
        }))
      );
    }

    if (isEditMode && values.claimEstate.estateId) return;

    // Backoffice em sinistro de condomínio deve escolher a imobiliária manualmente.
    const shouldAutoSelectEstate = !isBackOffice || !isCondominium;
    if (!shouldAutoSelectEstate) return;

    const resolvedEstateId = resolveClaimEstateId(
      user,
      availableBrokersAndEstates,
      estates
    );

    if (resolvedEstateId && resolvedEstateId !== values.claimEstate.estateId) {
      setFieldValue('claimEstate.estateId', resolvedEstateId);
    }
  }, [
    estates,
    availableBrokersAndEstates,
    user,
    setFieldValue,
    isEditMode,
    isBackOffice,
    isCondominium,
    values.claimEstate.estateId
  ]);

  useEffect(() => {
    if (!users?.length) return;
    setResponsibleOptions(
      users.map((user, index) => ({
        value: user.id,
        key: index,
        text: user.name
      }))
    );
  }, [users]);

  useEffect(() => {
    if (selectedFromWallet) return;
    if (values.claimProperty.zipCode && values.claimProperty.zipCode.length >= 8) {
      onChangeCep(values.claimProperty.zipCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.claimProperty.zipCode, selectedFromWallet]);

  async function getInsuredByDocument(document: string) {
    try {
      setLoadDocument(true);
      const unformatedDoc = document.replace(/\D/g, '');
      if (unformatedDoc.length !== 11 && unformatedDoc.length !== 14) {
        setDisableNameField(false);
        return;
      }

      const isCPF = unformatedDoc.length === 11;
      const data = await getDataByDocument(unformatedDoc);

      if (isCPF) {
        setFieldValue('claimTenant.fullName', data.name || '');
        setDisableNameField(false);
      } else {
        setFieldValue('claimTenant.fullName', data.company || '');
        if (data.phone) {
          setFieldValue('claimTenant.cellphone', maskPhoneNumber(data.phone));
        }
        if (data.email) {
          setFieldValue('claimTenant.email', data.email);
        }
        setDisableNameField(false);
      }
    } catch (err) {
      console.log(err);
      setDisableNameField(false);
    } finally {
      setLoadDocument(false);
    }
  }

  async function getCondominiumByDocument(document: string) {
    const unformattedDocument = document.replace(/\D/g, '');
    const requestId = condominiumLookupRequestRef.current + 1;
    condominiumLookupRequestRef.current = requestId;

    if (unformattedDocument.length !== 11 && unformattedDocument.length !== 14) {
      setLoadDocument(false);
      return;
    }

    try {
      setLoadDocument(true);
      const estateId = values.claimEstate.estateId || undefined;

      if (!isBackOffice && !estateId) {
        return;
      }

      const response = await lookupClaimCondominium(
        unformattedDocument,
        estateId,
        isBackOffice && !estateId
      );

      if (requestId !== condominiumLookupRequestRef.current || !response?.data) {
        return;
      }

      const data = response.data;
      if (isBackOffice && data.estate?.id) {
        setFieldValue('claimEstate.estateId', data.estate.id);
      }
      setCondominiumField('document', maskCpfOrCnpj(data.document || document));
      setCondominiumField('legalName', data.legalName || '');
      setCondominiumField('email', data.email || '');
      setCondominiumField('phone', maskPhoneNumber(data.phone || ''));

      setFieldValue('claimEstate.propertyPublicId', data.propertyPublicId);
      setFieldValue('claimProperty.propertyCode', data.identification || '');

      if (data.address) {
        setFieldValue(
          'claimProperty.zipCode',
          maskZipCode(data.address.zipcode || '')
        );
        setFieldValue('claimProperty.street', data.address.street || '');
        setFieldValue('claimProperty.district', data.address.neighborhood || '');
        setFieldValue('claimProperty.city', data.address.city || '');
        setFieldValue('claimProperty.state', data.address.state || '');
        setFieldValue('claimProperty.number', data.address.number || '');
        setFieldValue('claimProperty.complement', data.address.complement || '');
        setFieldValue('claimProperty.country', 'Brasil');
      }
    } catch (err) {
      if (requestId === condominiumLookupRequestRef.current) {
        console.log('condominium lookup error:', err);
      }
    } finally {
      if (requestId === condominiumLookupRequestRef.current) {
        setLoadDocument(false);
      }
    }
  }

  const onChangeCep = (zipCode: string): void => {
    const maskedValue = maskZipCode(zipCode);
    setFieldValue('claimProperty.zipCode', maskedValue);

    if (maskedValue.length >= 9) {
      setLoadingZipCode(true);
      AddressService.getAddressByZipCode(zipCode)
        .then(address => {
          setFieldValue('claimProperty.city', address.city);
          setFieldValue('claimProperty.district', address.district);
          setFieldValue('claimProperty.state', address.state);
          setFieldValue('claimProperty.street', address.street);
          setFieldValue('claimProperty.country', 'Brasil');
        })
        .catch(e => {
          console.log('address error:', e);
        })
        .finally(() => setLoadingZipCode(false));
    }
  };

  return (
    <div style={{ marginBottom: isCondominium ? '10px' : undefined }}>
      {isCondominium && values.claimEstate.estateId && (
        <ClaimPropertyWalletSearchDropdown
          estateId={values.claimEstate.estateId}
          setFieldValue={setFieldValue as any}
          values={values as any}
          hideLabel
          placeholder="Buscar condomínio por CNPJ, endereço ou apelido..."
          compact
        />
      )}
      {isCondominium ? (
        <>
          <ClaimSectionLabel text="Informações do Condomínio Segurado" />
          <Grid columns={16}>
            <Grid.Row columns={4} style={inputRowStyle}>
              <Grid.Column>
                <FieldLabel required>CPF/CNPJ</FieldLabel>
                <Field
                  name="claimCondominium.document"
                  value={values.claimCondominium.document}
                  component={Input}
                  loading={loadDocument}
                  fluid
                  onChange={(_e: any, { value }: { value: string }) => {
                    const nextDocument = maskCpfOrCnpj(value);
                    const previousDigits = values.claimCondominium.document.replace(
                      /\D/g,
                      ''
                    );
                    const nextDigits = nextDocument.replace(/\D/g, '');

                    setCondominiumField('document', nextDocument);
                    if (previousDigits !== nextDigits) {
                      condominiumLookupRequestRef.current += 1;
                      setLoadDocument(false);
                      clearCondominiumFields(true);
                      clearSelectedProperty();
                    }
                  }}
                  onBlur={(e: any) =>
                    getCondominiumByDocument(e.target.value)
                  }
                />
              </Grid.Column>
              <Grid.Column>
                <FieldLabel required>Razão social</FieldLabel>
                <Field
                  name="claimCondominium.legalName"
                  value={values.claimCondominium.legalName}
                  component={Input}
                  fluid
                  onChange={(_e: any, { value }: { value: string }) =>
                    setCondominiumField('legalName', value)
                  }
                />
              </Grid.Column>
              <Grid.Column>
                <FieldLabel required>E-mail</FieldLabel>
                <Field
                  name="claimCondominium.email"
                  value={values.claimCondominium.email}
                  component={Input}
                  fluid
                  onChange={(_e: any, { value }: { value: string }) =>
                    setCondominiumField('email', value)
                  }
                />
              </Grid.Column>
              <Grid.Column>
                <FieldLabel required>Telefone</FieldLabel>
                <Field
                  name="claimCondominium.phone"
                  value={values.claimCondominium.phone}
                  component={Input}
                  fluid
                  onChange={(_e: any, { value }: { value: string }) =>
                    setCondominiumField('phone', maskPhoneNumber(value))
                  }
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </>
      ) : (
        <>
          <ClaimSectionLabel text="Informações do Segurado" />
          <Grid columns={16}>
            <Grid.Row style={inputRowStyle}>
              <Grid.Column width={4}>
                <FieldLabel>CPF/CNPJ*</FieldLabel>
                <Field
                  value={values.claimTenant.document}
                  component={Input}
                  loading={loadDocument}
                  fluid
                  onChange={(_e: any, { value }: { value: string }) => {
                    setFieldValue('claimTenant.document', maskCpfOrCnpj(value));
                    setDisableNameField(true);
                  }}
                  onBlur={(e: any) => getInsuredByDocument(e.target.value)}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FieldLabel>Nome Completo*</FieldLabel>
                <Field
                  value={values.claimTenant.fullName}
                  component={Input}
                  fluid
                  disabled={disableNameField && !!values.claimTenant.document}
                  onChange={(_e: any, { value }: { value: string }) =>
                    setFieldValue('claimTenant.fullName', value)
                  }
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FieldLabel>E-mail*</FieldLabel>
                <Field
                  value={values.claimTenant.email}
                  component={Input}
                  fluid
                  onChange={(_e: any, { value }: { value: string }) =>
                    setFieldValue('claimTenant.email', value)
                  }
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FieldLabel>Telefone*</FieldLabel>
                <Field
                  value={values.claimTenant.cellphone}
                  component={Input}
                  fluid
                  onChange={(_e: any, { value }: { value: string }) =>
                    setFieldValue('claimTenant.cellphone', maskPhoneNumber(value))
                  }
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </>
      )}

      {isEditMode && (
        <>
          <ClaimSectionLabel text="Administração do Sinistro" />
          <Grid columns={16}>
            <Grid.Row style={inputRowStyle}>
              <Grid.Column width={4}>
                <FieldLabel>STATUS:</FieldLabel>
                <DropdownNativo
                  style={{ width: '100%' }}
                  value={values.claimEstate.status || ''}
                  name="claimEstate.status"
                  options={claimEstatesStatusEnum}
                  placeholder="Selecionar..."
                  selection
                  onChange={(_e, { value }) =>
                    setFieldValue('claimEstate.status', value)
                  }
                  clearable
                />
              </Grid.Column>
              {canSelectResponsible && (
                <Grid.Column width={4}>
                  <FieldLabel>Responsável:</FieldLabel>
                  <DropdownNativo
                    style={{ width: '100%' }}
                    name="claimEstate.responsibleId"
                    value={values.claimEstate.responsibleId || undefined}
                    search
                    clearable
                    placeholder="Selecione"
                    options={responsibleOptions}
                    onChange={(_e: SyntheticEvent, { value }) =>
                      setFieldValue('claimEstate.responsibleId', value || null)
                    }
                    selection
                  />
                </Grid.Column>
              )}
            </Grid.Row>
          </Grid>
        </>
      )}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: isCondominium ? '12px' : undefined
        }}
      >
        <div style={{ order: isCondominium ? 2 : 1 }}>
          <ClaimSectionLabel text="Informações do Seguro" />
          <Grid>
            <Grid.Row style={inputRowStyle}>
              <Grid.Column width={5}>
                <FieldLabel required>Seguradora/Garantidora</FieldLabel>
                <DropdownNativo
                  style={{ width: '100%' }}
                  name="claimEstate.insurerId"
                  value={values.claimEstate.insurerId}
                  options={selectableInsurers}
                  placeholder="Selecionar..."
                  search
                  selection
                  clearable
                  onChange={(_e, { value }) =>
                    setFieldValue('claimEstate.insurerId', value)
                  }
                />
              </Grid.Column>

              {!isEstateUser && selectableEstates.length > 0 && (
                <Grid.Column width={isCondominium ? 6 : 5}>
                  <FieldLabel required>Imobiliária</FieldLabel>
                  <DropdownNativo
                    style={{ width: '100%' }}
                    name="claimEstate.estateId"
                    value={values.claimEstate.estateId}
                    options={selectableEstates}
                    placeholder="Selecionar..."
                    search
                    selection
                    clearable
                    onChange={(_e, { value }) => {
                      const previousEstateId = values.claimEstate.estateId;
                      setFieldValue('claimEstate.estateId', value);
                      setFieldValue('claimEstate.estateResponsibleId', null);

                      if (previousEstateId !== value) {
                        if (isCondominium) {
                          if (values.claimEstate.propertyPublicId) {
                            clearSelectedProperty();
                          }
                        } else {
                          clearSelectedProperty();
                        }
                      }
                    }}
                  />
                </Grid.Column>
              )}

              <Grid.Column width={5}>
                <FieldLabel>Número da Apólice</FieldLabel>
                <Field
                  value={values.claimEstate.policyNumber || ''}
                  component={Input}
                  fluid
                  onChange={(_e: any, { value }: { value: string }) =>
                    setFieldValue('claimEstate.policyNumber', value || null)
                  }
                />
              </Grid.Column>
            </Grid.Row>
            {isEditMode && (
              <Grid.Row style={inputRowStyle}>
                <Grid.Column width={5}>
                  <FieldLabel>Código do Sinistro</FieldLabel>
                  <Field
                    value={values.claimEstate.claimCode || ''}
                    component={Input}
                    fluid
                    onChange={(_e: any, { value }: { value: string }) =>
                      setFieldValue('claimEstate.claimCode', value || null)
                    }
                  />
                </Grid.Column>
                <Grid.Column width={5}>
                  <FieldLabel>Número do Processo</FieldLabel>
                  <Field
                    value={values.claimEstate.processNumber || ''}
                    component={Input}
                    fluid
                    onChange={(_e: any, { value }: { value: string }) =>
                      setFieldValue('claimEstate.processNumber', value || null)
                    }
                  />
                </Grid.Column>
              </Grid.Row>
            )}
          </Grid>
        </div>

        <div style={{ order: isCondominium ? 1 : 2 }}>
          <ClaimSectionLabel
            text={isCondominium ? 'Endereço do condomínio' : 'Endereço'}
          />
          <Grid columns={16}>
            {!isCondominium && values.claimEstate.estateId && (
              <ClaimPropertyWalletSearchDropdown
                estateId={values.claimEstate.estateId}
                setFieldValue={setFieldValue as any}
                values={values as any}
              />
            )}
            {selectedFromWallet && (
              <Grid.Row style={{ marginTop: '-6px' }}>
                <Grid.Column width={16}>
                  <WalletTag>
                    Imóvel da carteira — dados bloqueados para edição
                  </WalletTag>
                </Grid.Column>
              </Grid.Row>
            )}
            <Grid.Row style={inputRowStyle}>
              <Grid.Column width={3}>
                <FieldLabel required>CEP</FieldLabel>
                <Field
                  name="claimProperty.zipCode"
                  value={values.claimProperty.zipCode}
                  loading={loadingZipCode}
                  component={Input}
                  maxLength={9}
                  fluid
                  disabled={isFieldLocked}
                  onChange={(_e: any, { value }: { value: string }) =>
                    setFieldValue('claimProperty.zipCode', maskZipCode(value))
                  }
                />
              </Grid.Column>
              <Grid.Column width={9}>
                <FieldLabel required>Logradouro</FieldLabel>
                <Field
                  name="claimProperty.street"
                  value={values.claimProperty.street}
                  component={Input}
                  fluid
                  disabled={isFieldLocked}
                  onChange={(_e: any, { value }: { value: string }) =>
                    setFieldValue('claimProperty.street', value)
                  }
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FieldLabel required>Número</FieldLabel>
                <Field
                  name="claimProperty.number"
                  value={values.claimProperty.number}
                  component={Input}
                  fluid
                  disabled={isFieldLocked}
                  onChange={(_e: any, { value }: { value: string }) =>
                    setFieldValue('claimProperty.number', value)
                  }
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row style={inputRowStyle}>
              <Grid.Column width={6}>
                <FieldLabel required>Cidade</FieldLabel>
                <Field
                  name="claimProperty.city"
                  value={values.claimProperty.city}
                  component={Input}
                  fluid
                  disabled={isFieldLocked}
                  onChange={(_e: any, { value }: { value: string }) =>
                    setFieldValue('claimProperty.city', value)
                  }
                />
              </Grid.Column>
              <Grid.Column width={6}>
                <FieldLabel required>Bairro</FieldLabel>
                <Field
                  name="claimProperty.district"
                  value={values.claimProperty.district}
                  component={Input}
                  fluid
                  disabled={isFieldLocked}
                  onChange={(_e: any, { value }: { value: string }) =>
                    setFieldValue('claimProperty.district', value)
                  }
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FieldLabel required>UF</FieldLabel>
                <DropdownNativo
                  style={{ width: '100%' }}
                  name="claimProperty.state"
                  value={values.claimProperty.state}
                  options={stateOptions}
                  placeholder="UF"
                  search
                  selection
                  clearable
                  disabled={isFieldLocked}
                  onChange={(_e, { value }) =>
                    setFieldValue('claimProperty.state', value)
                  }
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row style={inputRowStyle}>
              <Grid.Column width={8}>
                <FieldLabel>Complemento</FieldLabel>
                <Field
                  name="claimProperty.complement"
                  value={values.claimProperty.complement}
                  component={Input}
                  fluid
                  disabled={isFieldLocked}
                  onChange={(_e: any, { value }: { value: string }) =>
                    setFieldValue('claimProperty.complement', value)
                  }
                />
              </Grid.Column>
              <Grid.Column width={8}>
                <FieldLabel>
                  {isCondominium
                    ? 'Identificação do condomínio'
                    : 'Identificador do Imóvel'}
                </FieldLabel>
                <Field
                  name="claimProperty.propertyCode"
                  value={values.claimProperty.propertyCode || ''}
                  component={Input}
                  fluid
                  disabled={isFieldLocked}
                  onChange={(_e: any, { value }: { value: string }) =>
                    setFieldValue('claimProperty.propertyCode', value)
                  }
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
      </div>

      <ClaimSectionLabel text="Contatos para notificação" />
      <Grid columns="16" style={{ paddingLeft: '1em' }}>
        {(values.claimEstate.notificationEmails as string[]).map(
          (notificationEmail, index) => (
            <Grid.Row key={`notification-email-${index}`} style={inputRowStyle}>
              <Grid.Column width={4}>
                <Field
                  value={notificationEmail}
                  component={Input}
                  fluid
                  placeholder="E-mail"
                  onChange={(_e: any, { value }: { value: string }) => {
                    setFieldValue(`claimEstate.notificationEmails[${index}]`, value);
                  }}
                />
              </Grid.Column>
              <Grid.Column width={1}>
                <Icon
                  name="remove circle"
                  color="grey"
                  style={{ cursor: 'pointer', paddingTop: '5px' }}
                  size="large"
                  onClick={() => {
                    if (values.claimEstate.notificationEmails?.length === 1) {
                      setFieldValue('claimEstate.notificationEmails', ['']);
                    } else {
                      const arr1 = values.claimEstate.notificationEmails?.slice(0, index);
                      const arr2 = values.claimEstate.notificationEmails?.slice(index + 1);
                      setFieldValue('claimEstate.notificationEmails', [
                        ...(arr1 as string[]),
                        ...(arr2 as string[])
                      ]);
                    }
                  }}
                />
              </Grid.Column>
              {index + 1 === values.claimEstate.notificationEmails?.length && (
                <Grid.Column width={5}>
                  <Button
                    onClick={() => {
                      const emails = values.claimEstate.notificationEmails as string[];
                      const lastEmail = emails[emails.length - 1];
                      if (lastEmail?.length > 0) {
                        setFieldValue('claimEstate.notificationEmails', [...emails, '']);
                      }
                    }}
                  >
                    <Icon name="plus circle" />
                    Adicionar E-mail
                  </Button>
                </Grid.Column>
              )}
            </Grid.Row>
          )
        )}
      </Grid>

      {isEditMode && (
        <>
      <ClaimSectionLabel text="Observações" />
          <Grid columns="16" style={{ paddingLeft: '1em' }}>
            <Grid.Row style={inputRowStyle}>
              <Grid.Column width={16}>
                <Field
                  name="claimEstate.observations"
                  rows={3}
                  value={values.claimEstate.observations || ''}
                  onChange={(_: SyntheticEvent, data: any) => {
                    setFieldValue('claimEstate.observations', data.value);
                  }}
                  component={FormatedTextArea}
                  fluid
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </>
      )}
    </div>
  );
};

export default BasicInfoStep;
